import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useErpFields, useErpMutations, useErpProfiles, useErpRuns } from '../api'
import type { ErpColumnView } from '../types'

/**
 * Handing a posted journal to a finance system.
 *
 * <p>There is no "SAP" button. The customer maps columns onto the fields a journal actually
 * has, and the preview shows the real rows before anything is sent — because a file this
 * product shaped from a guess is a file somebody's ledger rejects at month end.
 */
export function ErpExportPage() {
  useBreadcrumb()
  const profiles = useErpProfiles()
  const fields = useErpFields()
  const runs = useErpRuns()
  const actions = useErpMutations()

  const [draft, setDraft] = useState<{
    id?: string; name: string; targetSystem: string; delimiter: string; dateFormat: string
    decimalSeparator: string; includeHeader: boolean; signedAmount: boolean
    columnMapping: ErpColumnView[]; destination: string; targetPath: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [banner] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="ERP export"
        subtitle="A posted journal, in the shape your finance system asked for."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                name: '', targetSystem: '', delimiter: ',', dateFormat: 'yyyy-MM-dd',
                decimalSeparator: '.', includeHeader: true, signedAmount: false,
                columnMapping: [{ column: 'Account', field: 'accountCode' }],
                destination: 'LOCAL', targetPath: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New profile
          </Button>
        }
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        No vendor format ships with this product. SAP, Oracle, Dynamics and Tally each want a
        different file, and this system does not know your chart of accounts — so you map the
        columns, and the preview shows exactly what will be written before anything leaves.
      </p>

      <Card>
        {profiles.isPending && <Skeleton className="m-4 h-20" />}
        {profiles.isError && <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />}
        {profiles.isSuccess && profiles.data.length === 0 && (
          <EmptyState
            title="No export profile"
            description="A profile says which journal field goes in which column, and where the file is delivered."
          />
        )}
        {profiles.isSuccess && profiles.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">Profile</th>
                  <th className="p-3 text-start font-medium">Columns</th>
                  <th className="p-3 text-start font-medium">Amounts</th>
                  <th className="p-3 text-start font-medium">Delivery</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {profiles.data.map((profile) => (
                  <tr key={profile.id} className="border-b border-[var(--color-border)] align-top">
                    <td className="p-3">
                      <p className="font-medium text-[var(--color-heading)]">{profile.name}</p>
                      {profile.targetSystem && (
                        <p className="text-xs text-[var(--color-muted)]">{profile.targetSystem}</p>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {profile.columnMapping.map((column) => column.column).join(', ')}
                    </td>
                    <td className="p-3 text-xs">
                      {profile.signedAmount ? 'One signed column' : 'Debit and credit'}
                    </td>
                    <td className="p-3 text-xs">
                      {profile.destination}
                      <span className="block text-[var(--color-muted)]">{profile.targetPath}</span>
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={profile.active ? 'ACTIVE' : 'OFF'}
                        tone={profile.active ? 'success' : 'neutral'}
                        label={profile.active ? 'Active' : 'Off'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <p className="border-b border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-heading)]">
          What has been sent
        </p>
        {runs.isPending && <Skeleton className="m-4 h-16" />}
        {runs.isSuccess && runs.data.length === 0 && (
          <EmptyState
            title="Nothing exported yet"
            description="A journal is exported from the payroll run that posted it. Re-exporting is allowed and recorded — an ERP that rejected a file needs it again."
          />
        )}
        {runs.isSuccess && runs.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">When</th>
                  <th className="p-3 text-start font-medium">File</th>
                  <th className="p-3 text-end font-medium">Rows</th>
                  <th className="p-3 text-end font-medium">Debit</th>
                  <th className="p-3 text-end font-medium">Credit</th>
                  <th className="p-3 text-start font-medium">Result</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.map((run) => (
                  <tr key={run.id} className="border-b border-[var(--color-border)]">
                    <td className="p-3"><DateValue value={run.createdAt} withTime /></td>
                    <td className="p-3 font-mono text-xs">{run.fileName}</td>
                    <td className="p-3 text-end">{run.rowCount}</td>
                    <td className="p-3 text-end">{run.totalDebit}</td>
                    <td className="p-3 text-end">{run.totalCredit}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={run.status}
                        tone={run.status === 'DELIVERED' ? 'success' : 'danger'}
                        label={run.status === 'DELIVERED' ? 'Delivered' : 'Failed'}
                      />
                      <span className="block max-w-sm truncate text-xs text-[var(--color-muted)]">
                        {run.error ?? run.destinationResult}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        title={draft?.id ? 'Edit profile' : 'New export profile'}
        submitLabel="Save"
        size="lg"
        loading={actions.save.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await actions.save.mutateAsync({
                ...draft, targetSystem: draft.targetSystem || null, active: true,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That profile could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="erp-name">Name</Label>
                <Input
                  id="erp-name" value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="erp-target">Which system it is for</Label>
                <Input
                  id="erp-target" value={draft.targetSystem} placeholder="A label, for people"
                  onChange={(event) => setDraft({ ...draft, targetSystem: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="erp-delimiter">Delimiter</Label>
                <Input
                  id="erp-delimiter" value={draft.delimiter}
                  onChange={(event) => setDraft({ ...draft, delimiter: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="erp-date">Date format</Label>
                <Input
                  id="erp-date" value={draft.dateFormat}
                  onChange={(event) => setDraft({ ...draft, dateFormat: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="erp-destination">Delivery</Label>
                <Select
                  id="erp-destination" value={draft.destination}
                  onChange={(event) => setDraft({ ...draft, destination: event.target.value })}
                >
                  <option value="LOCAL">A directory on this server</option>
                  <option value="SFTP">SFTP</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="erp-path">Where</Label>
                <Input
                  id="erp-path" value={draft.targetPath}
                  onChange={(event) => setDraft({ ...draft, targetPath: event.target.value })}
                />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox" className="mt-0.5 size-4" aria-label="One signed amount column"
                checked={draft.signedAmount}
                onChange={(event) => setDraft({
                  ...draft, signedAmount: event.target.checked,
                  columnMapping: draft.columnMapping.filter((column) =>
                    event.target.checked
                      ? column.field !== 'debit' && column.field !== 'credit'
                      : column.field !== 'amount'),
                })}
              />
              <span>
                One signed amount column
                <span className="block text-xs text-[var(--color-muted)]">
                  Positive is a debit, negative a credit. The alternative is separate debit and
                  credit columns; mixing the two makes a finance system import the value twice.
                </span>
              </span>
            </label>

            <div>
              <Label>Columns, in order</Label>
              <div className="mt-1 space-y-2">
                {draft.columnMapping.map((column, index) => (
                  <div key={index} className="flex flex-wrap items-end gap-2">
                    <Input
                      aria-label={`Column ${index + 1} heading`}
                      className="max-w-[12rem]"
                      value={column.column}
                      onChange={(event) => setDraft({
                        ...draft,
                        columnMapping: draft.columnMapping.map((existing, position) =>
                          position === index ? { ...existing, column: event.target.value } : existing),
                      })}
                    />
                    <Select
                      aria-label={`Column ${index + 1} field`}
                      value={column.field}
                      onChange={(event) => setDraft({
                        ...draft,
                        columnMapping: draft.columnMapping.map((existing, position) =>
                          position === index ? { ...existing, field: event.target.value } : existing),
                      })}
                    >
                      {fields.data?.map((field) => (
                        <option key={field.field} value={field.field}>{field.field}</option>
                      ))}
                    </Select>
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => setDraft({
                        ...draft,
                        columnMapping: draft.columnMapping.filter((_, position) => position !== index),
                      })}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button" variant="outline" size="sm" className="mt-2"
                onClick={() => setDraft({
                  ...draft,
                  columnMapping: [...draft.columnMapping, { column: '', field: 'accountCode' }],
                })}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add a column
              </Button>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
